import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import {
  cardFiltersToQueryParams,
  type CardTransactionQueryParams,
} from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import {
  type CardTransactionsSumResponse,
  getCardTransactionsSum,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { MS_TIME } from "Utils/TimeUtils";

const CARD_TRANSACTIONS_SUM_QUERY_KEY = "CARD_TRANSACTIONS_SUM";

export const cardTransactionsSumKeys = {
  all: [CARD_TRANSACTIONS_SUM_QUERY_KEY],
  byFilters: (params: CardTransactionQueryParams) => [
    ...cardTransactionsSumKeys.all,
    params,
  ],
};

type Props = {
  filters: CardTransactionFilters;
  enabled?: boolean;
};

export const useCardTransactionsSum = ({ filters, enabled = true }: Props) => {
  const queryParams = useMemo(() => {
    // The summary intentionally ignores the refund-only list filter — the endpoint neither accepts it,
    // so keying on it would only cause needless refetches of identical data.
    const { isRefundOrCancellation: _, ...sumParams } =
      cardFiltersToQueryParams(filters);
    return sumParams;
  }, [filters]);

  return useQuery<CardTransactionsSumResponse>({
    queryKey: cardTransactionsSumKeys.byFilters(queryParams),
    queryFn: processResponseWithApiProblemDetails(() =>
      getCardTransactionsSum(
        queryParams.dateFrom,
        queryParams.dateTo,
        queryParams.payPointPublicID,
        queryParams.employeePublicID,
        queryParams.status,
      ),
    ),

    enabled,
    refetchInterval: MS_TIME.halfMinute,
  });
};
